(function () {
  const LOOSE_UNIT_LIMIT = 4;
  let correcting = false;
  let addingLooseUnit = false;
  let timer = 0;

  function storefrontRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root.endsWith('/') ? root : root + '/';
  }

  async function request(path, payload) {
    const response = await fetch(storefrontRoot() + path, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'No se pudo ordenar el pack mayorista.');
    return result;
  }

  function invalidPackLines() {
    const lines = new Map();
    document.querySelectorAll('[data-dd-wholesale-pack-line]').forEach(function (row) {
      const key = row.dataset.key || '';
      const tier = Number(row.dataset.ddPackTier) || 0;
      const quantity = Number(row.dataset.ddPackQuantity) || 0;
      const variantId = Number(row.dataset.ddPackVariant) || 0;
      if (!key || !tier || !quantity || !variantId || quantity % tier === 0) return;
      lines.set(key, { key, tier, quantity, variantId });
    });
    return Array.from(lines.values());
  }

  function notifyThemeCart(cart) {
    const updatePromise = Promise.resolve({
      cart: { totalQuantity: Number(cart.item_count) || 0 },
      detail: {
        itemCount: Number(cart.item_count) || 0,
        items: cart.items || []
      }
    });
    const event = new Event('shopify:cart:lines-update', { bubbles: true });
    Object.defineProperties(event, {
      action: { value: 'update' },
      context: { value: 'cart' },
      lines: { value: [] },
      promise: { value: updatePromise }
    });
    document.dispatchEvent(event);
  }

  async function currentCart() {
    const response = await fetch(storefrontRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  }

  async function addLooseUnit(button) {
    if (addingLooseUnit || !(button instanceof HTMLButtonElement)) return;

    const row = button.closest('[data-dd-wholesale-pack-line]');
    const variantId = Number(button.dataset.ddWholesaleVariant || row?.dataset.ddPackVariant) || 0;
    const feedback = button.parentElement?.querySelector('[data-dd-wholesale-extra-feedback]');
    if (!variantId) return;

    addingLooseUnit = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (feedback) feedback.textContent = 'Agregando…';

    try {
      const cartBeforeAdd = await currentCart();
      const looseQuantity = (cartBeforeAdd.items || []).reduce(function (total, item) {
        const properties = item.properties || {};
        if (Number(item.variant_id) !== variantId || properties._dd_wholesale_mode === 'pack') return total;
        if (properties._dd_wholesale_mode !== 'assorted' && properties._dd_perfume !== 'true') return total;
        return total + (Number(item.quantity) || 0);
      }, 0);

      const looseLimit = Number(button.dataset.ddLooseLimit) || LOOSE_UNIT_LIMIT;
      if (looseQuantity >= looseLimit) {
        throw new Error(looseLimit === 1
          ? 'Ya tenés 1 unidad suelta. Para más, elegí el pack x2 en el producto.'
          : 'Para sumar 5 unidades, elegí otro pack x5. Así reciben el descuento correcto.');
      }

      await request('cart/add.js', {
        items: [{
          id: variantId,
          quantity: 1,
          properties: Object.assign({
            _dd_wholesale: 'true',
            _dd_wholesale_mode: 'assorted'
          }, button.dataset.ddLooseLimit === '1' ? { _dd_perfume: 'true', _dd_pf_line: 'disenador' } : {})
        }]
      });
      if (feedback) feedback.textContent = 'Agregada a precio de lista';
      notifyThemeCart(await currentCart());
    } catch (error) {
      if (feedback) feedback.textContent = error && error.message ? error.message : 'No se pudo agregar';
      console.warn('[DeusDream] No se pudo agregar la unidad a precio de lista:', error);
    } finally {
      window.setTimeout(function () {
        addingLooseUnit = false;
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }, 450);
    }
  }

  async function repairLine(line) {
    const cart = await currentCart();
    const current = cart.items.find(function (item) {
      return item.key === line.key;
    });
    if (!current) return;

    const properties = current.properties || {};
    const tier = Number(properties._dd_wholesale_tier) || line.tier;
    const quantity = Number(current.quantity) || line.quantity;
    if (!tier || quantity % tier === 0) return;

    const completePackQuantity = Math.floor(quantity / tier) * tier;
    const remainder = quantity - completePackQuantity;

    if (completePackQuantity === 0) {
      await request('cart/change.js', {
        id: current.key,
        quantity: quantity,
        properties: {
          _dd_wholesale: 'true',
          _dd_wholesale_mode: 'assorted'
        }
      });
      return;
    }

    await request('cart/change.js', {
      id: current.key,
      quantity: completePackQuantity
    });

    if (remainder > 0) {
      await request('cart/add.js', {
        items: [{
          id: current.variant_id,
          quantity: remainder,
          properties: {
            _dd_wholesale: 'true',
            _dd_wholesale_mode: 'assorted'
          }
        }]
      });
    }
  }

  /* Perfumes de diseñador: si un mismo perfume quedó con 2 o más unidades sueltas (sin pack), se reordena en
     packs x2 y como mucho 1 unidad suelta, para que el descuento quede siempre correcto. */
  function perfumeLooseGroups() {
    const groups = new Map();
    document.querySelectorAll('[data-dd-perfume-loose]').forEach(function (row) {
      const key = row.dataset.key || '';
      const variantId = Number(row.dataset.ddLooseVariant) || 0;
      const quantity = Number(row.dataset.ddLooseQty) || 0;
      if (!key || !variantId || !quantity) return;
      const group = groups.get(variantId) || { variantId: variantId, keys: new Set(), quantity: 0 };
      if (!group.keys.has(key)) { group.keys.add(key); group.quantity += quantity; }
      groups.set(variantId, group);
    });
    return Array.from(groups.values()).filter(function (g) { return g.quantity >= 2; });
  }

  function packToken(variantId, tier) {
    const rnd = window.crypto && typeof window.crypto.randomUUID === 'function' ? window.crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    return variantId + '-p' + tier + '-' + rnd;
  }

  async function regroupPerfume(group) {
    const cart = await currentCart();
    const loose = (cart.items || []).filter(function (item) {
      const p = item.properties || {};
      return Number(item.variant_id) === group.variantId && p._dd_wholesale_mode !== 'pack';
    });
    const total = loose.reduce(function (s, item) { return s + (Number(item.quantity) || 0); }, 0);
    if (total < 2) return;
    const updates = {};
    loose.forEach(function (item) { updates[item.key] = 0; });
    await request('cart/update.js', { updates: updates });
    const twos = Math.floor(total / 2);
    const single = total - twos * 2;
    const base = { _dd_perfume: 'true', _dd_wholesale: 'true', _dd_pf_line: 'disenador' };
    const items = [];
    for (let i = 0; i < twos; i += 1) items.push({ id: group.variantId, quantity: 2, properties: Object.assign({ _dd_wholesale_mode: 'pack', _dd_wholesale_tier: '02', _dd_wholesale_pack_id: packToken(group.variantId, 2) }, base) });
    if (single) items.push({ id: group.variantId, quantity: 1, properties: Object.assign({ _dd_wholesale_mode: 'assorted' }, base) });
    if (items.length) await request('cart/add.js', { items: items });
  }

  /* Árabes y body splash: con 6 o más unidades de la misma línea en el carrito (pueden ser distintas), cada línea
     lleva _dd_wholesale_tier "m6a" / "m6b" y SMART Functions aplica el descuento. Por debajo de 6 se quita. */
  const MIX_TIER = { arabe: 'm6a', body: 'm6b' };
  function mixMismatches() {
    const rows = Array.from(document.querySelectorAll('[data-dd-pf-mix]'));
    if (!rows.length) return [];
    const need = Number(document.documentElement.dataset.ddPfMixQty) || 6;
    const totals = { arabe: 0, body: 0 };
    const seen = new Set();
    rows.forEach(function (row) {
      const key = row.dataset.key || '';
      if (!key || seen.has(key)) return;
      seen.add(key);
      totals[row.dataset.ddPfMix] = (totals[row.dataset.ddPfMix] || 0) + (Number(row.dataset.ddPfQty) || 0);
    });
    const out = [];
    const done = new Set();
    rows.forEach(function (row) {
      const key = row.dataset.key || '';
      const line = row.dataset.ddPfMix;
      if (!key || done.has(key) || !MIX_TIER[line]) return;
      done.add(key);
      const target = totals[line] >= need ? MIX_TIER[line] : '';
      if ((row.dataset.ddPfTier || '') !== target) out.push({ key: key, line: line, target: target });
    });
    return out;
  }

  async function syncMix(fix) {
    const cart = await currentCart();
    const item = (cart.items || []).find(function (i) { return i.key === fix.key; });
    if (!item) return;
    const props = Object.assign({}, item.properties || {});
    props._dd_perfume = 'true';
    props._dd_wholesale = 'true';
    props._dd_wholesale_mode = 'mix';
    props._dd_pf_line = fix.line;
    if (fix.target) props._dd_wholesale_tier = fix.target; else delete props._dd_wholesale_tier;
    delete props._dd_wholesale_pack_id;
    await request('cart/change.js', { id: item.key, quantity: item.quantity, properties: props });
  }

  async function repairInvalidPacks() {
    if (correcting) return;
    const lines = invalidPackLines();
    const perfumeGroups = perfumeLooseGroups();
    const mixFixes = mixMismatches();
    if (!lines.length && !perfumeGroups.length && !mixFixes.length) return;

    correcting = true;
    try {
      for (const group of perfumeGroups) await regroupPerfume(group);
      for (const fix of mixFixes) await syncMix(fix);
      for (const line of lines) await repairLine(line);
      notifyThemeCart(await currentCart());
    } catch (error) {
      console.warn('[DeusDream] No se pudo normalizar una línea mayorista:', error);
    } finally {
      window.setTimeout(function () {
        correcting = false;
        scheduleRepair();
      }, 1400);
    }
  }

  function scheduleRepair() {
    window.clearTimeout(timer);
    timer = window.setTimeout(repairInvalidPacks, 220);
  }

  function invalidWholesaleMinimum() {
    return document.querySelector('[data-dd-wholesale-cart-minimum][data-dd-wholesale-valid="false"]');
  }

  function showMinimumRequirement(minimum) {
    if (!(minimum instanceof HTMLElement)) return;
    minimum.scrollIntoView({ behavior: 'smooth', block: 'center' });
    minimum.classList.remove('dd-wholesale-minimum--attention');
    window.requestAnimationFrame(function () {
      minimum.classList.add('dd-wholesale-minimum--attention');
      window.setTimeout(function () {
        minimum.classList.remove('dd-wholesale-minimum--attention');
      }, 900);
    });
  }

  function guardCheckout(event) {
    const minimum = invalidWholesaleMinimum();
    if (!minimum) return;

    const submitter = event.submitter;
    const isCartSubmit = event.type === 'submit'
      && event.target instanceof HTMLFormElement
      && event.target.id === 'cart-form';
    const checkoutTrigger = event.type === 'click' && event.target instanceof Element
      ? event.target.closest('[name="checkout"], [data-dd-wholesale-checkout], a[href$="/checkout"], a[href*="/checkout?"]')
      : null;

    if (!isCartSubmit && !checkoutTrigger && !(submitter instanceof HTMLElement && submitter.matches('[name="checkout"]'))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMinimumRequirement(minimum);
  }

  /* Perfumes: pago por transferencia. Arma el pedido completo y lo abre en el WhatsApp de DeusDream. */
  const AGENCY_FIELDS = [
    ['Departamento', 'Mayorista - Departamento'],
    ['Localidad', 'Mayorista - Localidad'],
    ['Agencia', 'Mayorista - Agencia preferida'],
    ['Sucursal', 'Mayorista - Sucursal'],
    ['Retira', 'Mayorista - Nombre de quien retira'],
    ['Cédula', 'Mayorista - Cédula de quien retira']
  ];
  const pesos = function (cents) { return '$' + Math.round((Number(cents) || 0) / 100).toLocaleString('es-UY'); };
  function agencyValue(attr, cart) {
    const field = document.querySelector('[name="attributes[' + attr + ']"]');
    const live = field && 'value' in field ? String(field.value || '').trim() : '';
    return live || String((cart.attributes || {})[attr] || '').trim();
  }
  function lineNote(item) {
    const p = item.properties || {};
    if (p._dd_wholesale_tier === '02') return 'pack x2';
    if (p._dd_wholesale_tier === 'm6a') return 'pack árabes surtidos';
    if (p._dd_wholesale_tier === 'm6b') return 'pack body splash surtidos';
    if (p._dd_wholesale_mode === 'pack' && p._dd_wholesale_tier) return 'pack x' + Number(p._dd_wholesale_tier);
    return '';
  }
  async function sendTransferOrder(button) {
    if (button.disabled) return;
    const minimum = invalidWholesaleMinimum();
    if (minimum || button.dataset.ddWholesaleValid === 'false') { showMinimumRequirement(minimum); return; }
    const number = (button.dataset.ddWaNumber || '').replace(/\D/g, '');
    if (!number) return;
    const popup = window.open('', '_blank');
    button.setAttribute('aria-busy', 'true');
    try {
      const cart = await currentCart();
      const agency = AGENCY_FIELDS.map(function (f) { return [f[0], agencyValue(f[1], cart)]; });
      const missing = agency.filter(function (f) { return !f[1]; });
      const form = document.querySelector('[data-dd-agency-order]');
      if (missing.length && form) {
        if (popup) popup.close();
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        form.classList.add('dd-wholesale-minimum--attention');
        window.setTimeout(function () { form.classList.remove('dd-wholesale-minimum--attention'); }, 900);
        const first = form.querySelector('[data-dd-agency-field]');
        if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
        return;
      }
      const lines = (cart.items || []).map(function (item) {
        const note = lineNote(item);
        return '• ' + item.quantity + ' x ' + item.product_title + (note ? ' (' + note + ')' : '') + ' — ' + pesos(item.final_line_price);
      });
      const msg = [
        '¡Hola DeusDream! Quiero pagar este pedido por *transferencia*:',
        '',
        lines.join('\n'),
        '',
        (cart.total_discount ? 'Descuentos: −' + pesos(cart.total_discount) + '\n' : '') + '*Total: ' + pesos(cart.total_price) + '*',
        '',
        'Envío por agencia:',
        agency.filter(function (f) { return f[1]; }).map(function (f) { return f[0] + ': ' + f[1]; }).join('\n'),
        cart.note ? '\nNota: ' + cart.note : '',
        '',
        '¿Me pasan los datos para transferir? Gracias.'
      ].join('\n').replace(/\n{3,}/g, '\n\n');
      const url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(msg);
      if (popup) popup.location.href = url; else window.location.href = url;
    } catch (error) {
      if (popup) popup.close();
      console.warn('[DeusDream] No se pudo armar el pedido por WhatsApp:', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  function initialize() {
    document.addEventListener('click', function (event) {
      const wa = event.target instanceof Element ? event.target.closest('[data-dd-wa-transfer]') : null;
      if (!(wa instanceof HTMLButtonElement)) return;
      event.preventDefault();
      sendTransferOrder(wa);
    });
    document.addEventListener('submit', guardCheckout, true);
    document.addEventListener('click', guardCheckout, true);

    document.addEventListener('click', function (event) {
      const target = event.target instanceof Element
        ? event.target.closest('[data-dd-wholesale-add-loose]')
        : null;
      if (!(target instanceof HTMLButtonElement)) return;
      event.preventDefault();
      event.stopPropagation();
      addLooseUnit(target);
    });

    scheduleRepair();
    const observer = new MutationObserver(scheduleRepair);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
